import { Preferences } from '@capacitor/preferences';
import { useEffect, useState } from 'react';

/**
 * Like usePreferences but with a typed API and a default value.
 * The caller works directly with values of type T — serialisation/deserialisation
 * is handled internally. Writes perform an optimistic local update immediately so
 * rapid toggles are never overwritten by a stale async round-trip.
 *
 * Pass an optional `refreshTrigger` to re-read from storage whenever that value
 * changes (e.g. pass the modal's `open` prop so the hook picks up preference
 * changes made in other mounted instances of the same component).
 */
export function usePreferenceQuery<T>(key: string, defaultValue: T, refreshTrigger?: unknown) {
  const [value, setValue] = useState<T>(defaultValue);

  useEffect(() => {
    Preferences.get({ key }).then(result => {
      if (result.value !== null) {
        try {
          setValue(JSON.parse(result.value));
        } catch {
          // Corrupted value: fall back to default
        }
      }
    });
  }, [key, refreshTrigger]);

  const set = (newValue: T) => {
    setValue(newValue);
    Preferences.set({ key, value: JSON.stringify(newValue) });
  };

  return { value, set };
}
